import React, { memo, useEffect, useMemo, useCallback } from 'react'
import { Card, Checkbox, Form, Switch, Select, Tag } from 'antd'
import type { DefaultOptionType } from 'antd/lib/select'
import { useForm } from 'antd/lib/form/Form'
import { extract } from '../../../../../_utils/object'
import { canShowField } from '../helpers'
import { INotificationFieldSuggestions } from '../../../../../interfaces/notification-field-suggestions'
import { SuggestedFieldsForm, VariantCardProps } from '../types'
import { BulbFilled } from '@ant-design/icons'
import { titleCase } from 'change-case'

/**
 * VariantCard component
 *   Card view for AI-generated variants with unified enable/disable switch and action selector
 */
export const VariantCard = memo(
    ({
        variant,
        variantIndex,
        domain,
        isEnabled,
        selectedAction,
        disabledReplaceActions,
        maxVariants,
        currentVariantCount,
        builderVariantCount,
        onEnabledChange,
        onActionChange,
        onFieldSelectionsChange,
    }: VariantCardProps) => {
        const [form] = useForm<SuggestedFieldsForm>()

        // Use builderVariantCount for replace options, fallback to currentVariantCount if not provided
        const actualBuilderVariantCount = builderVariantCount ?? currentVariantCount

        const cardSuggestedImage = useMemo<string | null>(() => {
            if (!variant?.imageUrl && !variant?.compositeImage) return null
            if (variant?.compositeImage) return extract(variant.compositeImage, 'url')
            return variant.imageUrl
        }, [variant?.imageUrl, variant?.compositeImage])

        const hasTitleSuggestion = !!variant?.title
        const hasBodySuggestion = !!variant?.body
        const hasImageSuggestion = cardSuggestedImage !== null
        const hasReason = !!variant?.reason

        // Helper function to build selections from current form values
        const buildSelectionsFromForm = useCallback((): Partial<INotificationFieldSuggestions> => {
            const values = form.getFieldsValue()
            const selections: Partial<INotificationFieldSuggestions> = {}

            if (values.title && canShowField(domain, 'title')) {
                selections.title = variant.title
            }
            if (values.body && canShowField(domain, 'body')) {
                selections.body = variant.body
            }
            if (values.imageUrl && canShowField(domain, 'imageUrl')) {
                if (variant.compositeImage) {
                    selections.compositeImage = variant.compositeImage
                    selections.imageUrl = variant.imageUrl
                } else {
                    selections.imageUrl = cardSuggestedImage
                }
            }

            return selections
        }, [form, domain, variant, cardSuggestedImage, variantIndex])

        // Initialize form with default selections
        useEffect(() => {
            const initialValues = {
                title: hasTitleSuggestion,
                body: hasBodySuggestion,
                imageUrl: hasImageSuggestion,
            }

            form.setFieldsValue(initialValues)

            // Build initial selections based on the values we just set
            const selections: Partial<INotificationFieldSuggestions> = {}

            if (initialValues.title && canShowField(domain, 'title')) {
                selections.title = variant.title
            }
            if (initialValues.body && canShowField(domain, 'body')) {
                selections.body = variant.body
            }
            if (initialValues.imageUrl && canShowField(domain, 'imageUrl')) {
                if (variant.compositeImage) {
                    selections.compositeImage = variant.compositeImage
                    selections.imageUrl = variant.imageUrl
                } else {
                    selections.imageUrl = cardSuggestedImage
                }
            }

            onFieldSelectionsChange(variantIndex, selections)
        }, [
            variantIndex,
            hasTitleSuggestion,
            hasBodySuggestion,
            hasImageSuggestion,
            variant,
            domain,
            cardSuggestedImage,
            onFieldSelectionsChange,
            form,
        ])

        // Update selections whenever form values change
        const handleFormChange = useCallback(() => {
            const selections = buildSelectionsFromForm()
            onFieldSelectionsChange(variantIndex, selections)
        }, [variantIndex, onFieldSelectionsChange, buildSelectionsFromForm])

        // New event handlers for unified action interface
        const handleEnabledChange = useCallback(
            (checked: boolean) => {
                onEnabledChange(variantIndex, checked)

                if (checked) {
                    // Only auto-select "Add as new" when enabled if add slots are available
                    if (currentVariantCount < maxVariants) {
                        onActionChange(variantIndex, { type: 'add' })
                    } else {
                        // No add slots available, don't auto-select any action
                        onActionChange(variantIndex, null)
                    }
                } else {
                    // Reset field selections to defaults (all checked) on disable
                    const resetValues = {
                        title: hasTitleSuggestion,
                        body: hasBodySuggestion,
                        imageUrl: hasImageSuggestion,
                    }
                    form.setFieldsValue(resetValues)

                    // Rebuild selections from reset values
                    const selections: Partial<INotificationFieldSuggestions> = {}
                    if (resetValues.title && canShowField(domain, 'title')) {
                        selections.title = variant.title
                    }
                    if (resetValues.body && canShowField(domain, 'body')) {
                        if (variant.compositeImage) {
                            selections.compositeImage = variant.compositeImage
                            selections.imageUrl = variant.imageUrl
                        } else {
                            selections.imageUrl = cardSuggestedImage
                        }
                    }
                    if (resetValues.imageUrl && canShowField(domain, 'imageUrl')) {
                        if (variant.compositeImage) {
                            selections.compositeImage = variant.compositeImage
                            selections.imageUrl = variant.imageUrl
                        } else {
                            selections.imageUrl = cardSuggestedImage
                        }
                    }

                    onFieldSelectionsChange(variantIndex, selections)
                }
            },
            [
                variantIndex,
                onEnabledChange,
                onActionChange,
                onFieldSelectionsChange,
                currentVariantCount,
                maxVariants,
                form,
                hasTitleSuggestion,
                hasBodySuggestion,
                hasImageSuggestion,
                domain,
                variant,
                cardSuggestedImage,
            ],
        )

        const handleActionChange = useCallback(
            (value: string) => {
                if (value === 'add') {
                    onActionChange(variantIndex, { type: 'add' })
                } else if (value.startsWith('replace-')) {
                    const targetIndex = parseInt(value.replace('replace-', ''), 10)
                    onActionChange(variantIndex, { type: 'replace', targetIndex })
                }
            },
            [variantIndex, onActionChange],
        )

        // Generate dropdown options with grouped categories
        const dropdownOptions = useMemo((): DefaultOptionType[] => {
            const canAdd = currentVariantCount < maxVariants

            const groups: DefaultOptionType[] = []

            // "Add New" group
            groups.push({
                label: 'Add New',
                options: [
                    {
                        value: 'add',
                        label: 'Add as new notification',
                        disabled: !canAdd,
                    },
                ],
            })

            // "Replace Existing" group (only if there are builder variants)
            if (actualBuilderVariantCount > 0) {
                groups.push({
                    label: 'Replace Existing',
                    options: Array.from({ length: actualBuilderVariantCount }, (_, i) => ({
                        value: `replace-${i}`,
                        label: `Replace notification ${i + 1}`,
                        disabled: disabledReplaceActions.includes(i),
                    })),
                })
            }

            return groups
        }, [currentVariantCount, maxVariants, actualBuilderVariantCount, disabledReplaceActions])

        // Determine dropdown value and placeholder
        const dropdownValue = useMemo(() => {
            if (!isEnabled) return undefined
            if (!selectedAction) return undefined
            if (selectedAction.type === 'add') return 'add'
            return `replace-${selectedAction.targetIndex}`
        }, [isEnabled, selectedAction])

        const dropdownPlaceholder = useMemo(() => {
            if (!isEnabled) return 'Variant not enabled'
            return 'Select an action'
        }, [isEnabled])

        // Click handlers for field content
        const handleTitleClick = useCallback(() => {
            const currentValue = form.getFieldValue('title')
            form.setFieldsValue({ title: !currentValue })
            // Trigger form change to update selections
            setTimeout(() => handleFormChange(), 0)
        }, [form, handleFormChange])

        const handleBodyClick = useCallback(() => {
            const currentValue = form.getFieldValue('body')
            form.setFieldsValue({ body: !currentValue })
            // Trigger form change to update selections
            setTimeout(() => handleFormChange(), 0)
        }, [form, handleFormChange])

        const handleImageClick = useCallback(() => {
            const currentValue = form.getFieldValue('imageUrl')
            form.setFieldsValue({ imageUrl: !currentValue })
            // Trigger form change to update selections
            setTimeout(() => handleFormChange(), 0)
        }, [form, handleFormChange])

        const hasTone = !!variant?.tone

        const cardTitle = useMemo(
            () => (
                <div className="variant-card-title">
                    <div className="variant-card-title-controls">
                        <Switch
                            size="small"
                            checked={isEnabled}
                            onChange={handleEnabledChange}
                            style={{ marginRight: 8 }}
                        />
                        <Select
                            size="small"
                            style={{ flex: 1, maxWidth: '200px' }}
                            value={dropdownValue}
                            placeholder={dropdownPlaceholder}
                            onChange={handleActionChange}
                            disabled={!isEnabled}
                            options={dropdownOptions}
                        />
                    </div>
                    {hasTone && (
                        <Tag color="blue" className="variant-tone">
                            {titleCase(variant.tone!)}
                        </Tag>
                    )}
                </div>
            ),
            [
                isEnabled,
                dropdownValue,
                dropdownPlaceholder,
                dropdownOptions,
                handleEnabledChange,
                handleActionChange,
                hasTone,
                variant?.tone,
            ],
        )

        // Determine CSS classes based on enabled state and selected action
        const cardClassName = useMemo(() => {
            const classes = ['variant-card-2']

            if (isEnabled && selectedAction) {
                classes.push('selected-variant')
            }

            return classes.join(' ')
        }, [isEnabled, selectedAction])

        return (
            <Card className={cardClassName} title={cardTitle} size="small" style={{ height: '100%' }}>
                <Card className="variant-card-2-content" type="inner" size="small">
                    {hasReason && (
                        <div className="field-section reason-section block-quote">
                            <div className="reason-label">Why this Variant?</div>
                            <div className="reason-text">
                                <BulbFilled />
                                &nbsp;
                                <span>{variant.reason}</span>
                            </div>
                        </div>
                    )}

                    <Form form={form} size="small" onValuesChange={handleFormChange}>
                        {hasTitleSuggestion && canShowField(domain, 'title') && (
                            <div className="field-section">
                                <Form.Item name="title" valuePropName="checked">
                                    <Checkbox disabled={!isEnabled}>Title</Checkbox>
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.title !== curr.title}>
                                    {({ getFieldValue }) => (
                                        <div
                                            className={`field-preview content-clickable ${
                                                !getFieldValue('title') || !isEnabled ? 'disabled' : ''
                                            }`}
                                            onClick={isEnabled ? handleTitleClick : undefined}
                                        >
                                            {variant.title}
                                        </div>
                                    )}
                                </Form.Item>
                            </div>
                        )}

                        {hasBodySuggestion && canShowField(domain, 'body') && (
                            <div className="field-section">
                                <Form.Item name="body" valuePropName="checked">
                                    <Checkbox disabled={!isEnabled}>Body</Checkbox>
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.body !== curr.body}>
                                    {({ getFieldValue }) => (
                                        <div
                                            className={`field-preview content-clickable ${
                                                !getFieldValue('body') || !isEnabled ? 'disabled' : ''
                                            }`}
                                            onClick={isEnabled ? handleBodyClick : undefined}
                                        >
                                            {variant.body}
                                        </div>
                                    )}
                                </Form.Item>
                            </div>
                        )}

                        {hasImageSuggestion && canShowField(domain, 'imageUrl') && (
                            <div className="field-section">
                                <Form.Item name="imageUrl" valuePropName="checked">
                                    <Checkbox disabled={!isEnabled}>Image</Checkbox>
                                </Form.Item>
                                <Form.Item noStyle shouldUpdate={(prev, curr) => prev.imageUrl !== curr.imageUrl}>
                                    {({ getFieldValue }) => (
                                        <div
                                            className={`field-preview image-preview content-clickable ${
                                                !getFieldValue('imageUrl') || !isEnabled ? 'disabled' : ''
                                            }`}
                                            onClick={isEnabled ? handleImageClick : undefined}
                                        >
                                            <div
                                                className="suggested-image-small"
                                                style={{ backgroundImage: `url('${cardSuggestedImage}')` }}
                                            />
                                        </div>
                                    )}
                                </Form.Item>
                            </div>
                        )}
                    </Form>
                </Card>
            </Card>
        )
    },
)

VariantCard.displayName = 'VariantCard'
